// Scoped behavior around Horizon's slideshow and product hotspots.
// Rotation, swipe, arrows, product selection and cart writes stay native.
class PocoEditorialCarousel extends HTMLElement {
  async connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;
    await customElements.whenDefined('slideshow-component');
    if (signal.aborted || !this.isConnected) return;
    this.carousel = this.querySelector('slideshow-component');
    if (!this.carousel) return;
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    const on = (node, event, handler, options = {}) => node.addEventListener(event, handler, { ...options, signal });
    const pause = () => { this.carousel.pause(); this.clearHint(); };
    on(this, 'focusin', pause);
    on(this, 'pointerdown', event => {
      const button = event.target.closest('button');
      if (button?.getAttribute('on:click') === '/play') return;
      pause();
    }, { capture: true });
    on(this, 'click', () => queueMicrotask(() => this.syncVisibility()));
    on(this.carousel, 'mouseleave', () => queueMicrotask(() => this.syncVisibility()));
    on(document, 'visibilitychange', () => queueMicrotask(() => this.syncVisibility()));
    on(this.motion, 'change', () => {
      if (this.motion.matches) pause();
    });
    this.observer = new MutationObserver(() => this.syncSlide());
    this.observer.observe(this.carousel, { subtree: true, attributes: true, attributeFilter: ['aria-hidden', 'paused'] });
    this.visibility = new IntersectionObserver(entries => {
      const wasVisible = this.inView;
      this.inView = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.15;
      this.syncVisibility();
      if (this.inView && !wasVisible) this.hint();
      else if (!this.inView) this.clearHint();
    }, { threshold: [0, 0.15] });
    this.visibility.observe(this);
    this.inView = false;
    if (this.motion.matches || window.Shopify?.designMode) this.carousel.pause();
    else this.carousel.suspend();
    this.syncSlide();
  }

  syncVisibility() {
    if (!this.carousel) return;
    if (!this.inView || document.hidden) this.carousel.suspend();
    else this.carousel.resume();
  }

  syncSlide() {
    const slides = [...this.carousel.querySelectorAll(':scope > slideshow-container > slideshow-slides > slideshow-slide')];
    const active = slides.find(slide => slide.getAttribute('aria-hidden') === 'false');
    slides.forEach(slide => { slide.inert = slide !== active; });
    this.carousel.setAttribute('aria-live', this.carousel.paused ? 'polite' : 'off');
    if (active !== this.active) {
      this.active = active;
      this.hint();
    }
  }

  clearHint() {
    clearTimeout(this.hintTimer);
    this.querySelectorAll('[data-pb-hotspot-hint]').forEach(slide => slide.removeAttribute('data-pb-hotspot-hint'));
  }

  hint() {
    this.clearHint();
    if (!this.inView || !this.active || this.motion.matches || document.hidden) return;
    this.active.setAttribute('data-pb-hotspot-hint', '');
    this.hintTimer = setTimeout(() => this.clearHint(), 2200);
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.observer?.disconnect();
    this.visibility?.disconnect();
    this.carousel?.suspend();
    this.clearHint();
  }
}

if (!customElements.get('poco-editorial-carousel')) customElements.define('poco-editorial-carousel', PocoEditorialCarousel);
